import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import { useAuth } from "../../context/AuthContext";
import { useEventos } from "../../context/EventosContext";
import { useUsers } from "../../context/UsersContext";
import { useNotas } from "../../context/NotasContext";
import { FaSpinner, FaTimes } from "react-icons/fa"; // Ícono de carga

const DetallesModal = ({ isOpen, evento, onClose }) => {
  const { user } = useAuth();
  const { updateEventoById, deleteEventoById } = useEventos();
  const { salones, asignaturas, fetchSalones, fetchAsignaturas } = useUsers();
  const { getAsignaturasSalon, asignaturasSalon, loadingAsignaturas } = useNotas();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    titulo: "",
    fecha: "",
    descripcion: "",
    salon_id: null,
    asignatura_id: null,
  });

  const [existingPhotos, setExistingPhotos] = useState([]); // Fotos ya almacenadas
  const [newPhotos, setNewPhotos] = useState([]); // Fotos nuevas
  const [isUserAuthorized, setIsUserAuthorized] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Nuevo estado de carga
  const [isExpanded, setIsExpanded] = useState(false); // Estado para ver más/ver menos
  const maxLength = 2000;

  // ✅ AGREGAR: Referencia para ReactQuill
  const quillRef = useRef(null);


  // Formatear fecha para input
  const formatDateForInput = (isoDate) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    return date.toISOString().split("T")[0];
  };

  // ✅ CARGAR DATOS cuando se abre el modal o cambia isEditing
  useEffect(() => {
    if (evento) {
      setFormData({
        titulo: evento.titulo || "",
        fecha: evento.fecha ? formatDateForInput(evento.fecha) : "",
        descripcion: evento.descripcion || "",
        salon_id: evento.salon_id || null,
        asignatura_id: evento.asignatura_id || null,
      });

      // ✅ FORZAR CARGA EN QUILL cuando entra en modo edición
      if (isEditing && quillRef.current) {
        const editor = quillRef.current.getEditor();
        const contenido = evento.descripcion || "";
        editor.clipboard.dangerouslyPasteHTML(contenido);
      }

      const esCreador = evento.users_id === user.id;
      const esRector = user.rol_id === 1;
      const esDirectorDelSalon =
        user.rol_id === 2 &&
        user.director_salon &&
        evento.salon_id === user.director_salon;

      setIsUserAuthorized(esCreador || esRector || esDirectorDelSalon);
      setExistingPhotos(evento.fotos || []);
    }
  }, [evento, user.id, user.rol_id, user.director_salon, isEditing]);


  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false);
      setNewPhotos([]);
      document.body.classList.remove("overflow-hidden"); // Habilitar scroll en el fondo
    } else {
      document.body.classList.add("overflow-hidden"); // Evita scroll en el fondo
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && salones.length === 0) fetchSalones();
  }, [isOpen, salones.length, fetchSalones]);

  // ✅ NUEVO: Cargar asignaturas cuando se seleccione salón en modo edición
  useEffect(() => {
    if (isEditing && formData.salon_id) {
      getAsignaturasSalon(formData.salon_id);
    }
  }, [isEditing, formData.salon_id, getAsignaturasSalon]);

  // ✅ NUEVO: Función para obtener grado del salón
  const getSalonGrado = () => {
    if (formData.salon_id) {
      const salon = salones.find(s => s.id === parseInt(formData.salon_id));
      return salon?.grado?.toLowerCase();
    }
    return null;
  };

  const gradoActual = getSalonGrado();
  const esPreescolar = ["parvulos", "prejardin", "jardin", "transicion"].includes(gradoActual);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // ✅ AGREGAR: Resetear asignatura al cambiar salón
    if (name === "salon_id") {
      setFormData((prev) => ({ 
        ...prev, 
        [name]: value,
        asignatura_id: "" // Resetear asignatura
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDescripcionChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      descripcion: value,
    }));
  };

  // Manejar subida de nuevas fotos
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setNewPhotos((prev) => [...prev, ...files]); // Agregar nuevas fotos sin eliminar las existentes
  };

  // Enviar formulario con imágenes usando FormData
  const handleUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true); // Activar animación de carga
    try {
      const data = new FormData();
      data.append("titulo", formData.titulo);
      data.append("fecha", formData.fecha);
      data.append("descripcion", formData.descripcion);
      if (formData.salon_id) data.append("salon_id", formData.salon_id);
      if (formData.asignatura_id)
        data.append("asignatura_id", formData.asignatura_id);

      // Agregar imágenes al FormData correctamente
      newPhotos.forEach((file) => {
        data.append("fotos", file); // Esto asegura que se envíen archivos
      });

      await updateEventoById(evento.id, data);
      
      // ✅ Limpiar editor al guardar
      if (quillRef.current) {
        const editor = quillRef.current.getEditor();
        editor.setText("");
      }

      setIsEditing(false);
      onClose();
    } catch (error) {
    } finally {
      setIsLoading(false); // Desactivar animación de carga
    }
  };

  const handleDelete = async () => {
    try {
      await deleteEventoById(evento.id);
      onClose();
    } catch (error) {
    }
  };

  if (!isOpen || !evento) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-2xl relative">
        <div className="bg-blue-900 text-white p-3 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-bold">
            {formData.titulo || "Sin título"}
          </h2>
          <button onClick={onClose} className="text-white">
            <FaTimes />
          </button>
        </div>
        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {!isEditing ? (
            <>
              <div className="text-left">
                <p>
                  <strong className="text-base font-bold">Fecha:</strong>{" "}
                  {formData.fecha || "Fecha no disponible"}
                </p>
                {evento.salon_nombre && (
                    <p>
                      <strong>Salón:</strong> {evento.salon_nombre}
                    </p>
                  )}
                  {evento.asignatura_nombre && (
                    <p>
                      <strong>Asignatura:</strong> {evento.asignatura_nombre}
                    </p>
                  )}
                <strong className="text-base font-bold">Descripción:</strong>{" "}
                <div className="text-sm text-gray-700 mt-2 text-left">
                  <div
                    className="evento-descripcion text-sm text-gray-700 mt-2 space-y-2"
                    dangerouslySetInnerHTML={{
                      __html: isExpanded
                        ? formData.descripcion
                        : `${formData.descripcion.slice(0, 100)}...`,
                    }}
                  />
                  {formData.descripcion.length > 100 && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="text-gray font-semibold ml-2"
                    >
                      {isExpanded ? "Ver menos" : "Ver más"}
                    </button>
                  )}
                  
                </div>
              </div>

              {/* Mostrar fotos existentes */}
              {(existingPhotos.length > 0 || newPhotos.length > 0) && (
                <div className="mt-4 text-center">
                  <strong>Fotos:</strong>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                    {existingPhotos.map((foto, index) => (
                      <img
                        key={index}
                        src={foto}
                        alt={`Foto ${index + 1}`}
                        className="w-full h-20 object-cover rounded cursor-pointer"
                        onClick={() => window.open(foto, "_blank")}
                      />
                    ))}
                    {newPhotos.map((file, index) => (
                      <img
                        key={`new-${index}`}
                        src={URL.createObjectURL(file)}
                        alt={`Foto nueva ${index + 1}`}
                        className="w-16 h-16 object-cover rounded border-2 border-green-500 cursor-pointer"
                        onClick={() =>
                          window.open(URL.createObjectURL(file), "_blank")
                        }
                      />
                    ))}
                  </div>
                </div>
              )}

              {isUserAuthorized && (
                <div className="flex justify-between mt-4">
                  <button
                    onClick={() => setIsEditing(true)}
                    className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
                  >
                    Editar
                  </button>
                  <button
                    onClick={handleDelete}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <FaSpinner className="animate-spin mr-2" />
                    ) : (
                      "Eliminar"
                    )}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="max-h-[80vh]">
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-bold mb-2 text-blue-900">
                    Título
                  </label>
                  <input
                    type="text"
                    name="titulo"
                    value={formData.titulo}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-blue-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2 text-blue-900">
                    Fecha
                  </label>
                  <input
                    type="date"
                    name="fecha"
                    value={formData.fecha}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border rounded focus:outline-blue-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-2 text-blue-900">
                    Descripción
                  </label>
                  <ReactQuill
                    value={formData.descripcion}
                    onChange={handleDescripcionChange}
                    className="bg-white rounded-lg"
                    theme="snow"
                    placeholder="Escribe la descripción aquí..."
                    modules={{
                      toolbar: [
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["bold", "italic", "underline"],
                        ["link"],
                        ["clean"],
                      ],
                    }}
                  />
                  <div className="text-right text-gray-500 text-sm">
                    {formData.descripcion.length}/{maxLength}
                  </div>
                </div>

                {formData.salon_id !== null && (
                  <div>
                    <label className="block text-sm font-bold mb-2 text-blue-900">
                      Salón
                    </label>
                    <select
                      name="salon_id"
                      value={formData.salon_id}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:outline-blue-800"
                    >
                      <option value="">Selecciona un salón</option>
                      {salones.map((salon) => (
                        <option key={salon.id} value={salon.id}>
                          {salon.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {formData.asignatura_id !== null && (
                  <div>
                    <label className="block text-sm font-bold mb-2 text-blue-900">
                      Asignatura
                    </label>
                    <select
                      name="asignatura_id"
                      value={formData.asignatura_id}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:outline-blue-800"
                    >
                      <option value="">
                        {loadingAsignaturas ? "Cargando asignaturas..." : "Selecciona una asignatura"}
                      </option>
                      {asignaturasSalon
                        .filter((asignatura) => {
                          // ✅ USAR LÓGICA DE FILTRADO IGUAL QUE EN NotasForm
                          if (esPreescolar) {
                            return asignatura.descripcion && asignatura.descripcion.trim() !== "";
                          } else {
                            return asignatura.IH && !isNaN(parseInt(asignatura.IH)) && parseInt(asignatura.IH) > 0;
                          }
                        })
                        .sort((a, b) => {
                          if (esPreescolar) {
                            return a.nombre.localeCompare(b.nombre);
                          } else {
                            const ihDiff = parseInt(b.IH || 0) - parseInt(a.IH || 0);
                            return ihDiff !== 0 ? ihDiff : a.nombre.localeCompare(b.nombre);
                          }
                        })
                        .map((asignatura) => (
                          <option key={asignatura.asignatura_id} value={asignatura.asignatura_id}>
                            {asignatura.nombre}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <div className="mt-4">
                  <label className="block text-sm font-bold mb-2 text-blue-900">
                    Fotos (opcional)
                  </label>
                  <input
                    type="file"
                    name="fotos"
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="text-gray cursor-pointer border-2 border-dashed border-gray rounded-lg p-6 w-full text-center focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-center"
                  >
                    Subir
                  </label>

                  {/* Previsualización de imágenes seleccionadas */}
                  <div className="flex space-x-4 mt-4 flex-wrap">
                    {newPhotos.map((file, index) => (
                      <div key={index} className="relative">
                        <img
                          key={index}
                          src={URL.createObjectURL(file)}
                          alt={`Preview ${index + 1}`}
                          className="w-20 h-20 object-cover rounded shadow"
                        />
                        {/* Botón para eliminar la foto */}
                        <button
                          type="button"
                          onClick={() =>
                            setNewPhotos((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full px-2 py-1 hover:bg-red-600"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={handleUpdate}
                    className="bg-main-bg-sidebar text-white px-4 py-2 mb-2 rounded-lg hover:bg-blue-800 "
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <FaSpinner className="animate-spin mr-2" />
                    ) : (
                      "Guardar"
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetallesModal;
